import {
  AssessmentDisplayStatus,
  ChakraBox,
  ChakraFlex,
  ChakraText,
  EquipUIFireflyV2Theme,
  EquipUITheme,
  Modal,
} from '@equip.health/ui';
import { DateTime } from 'luxon';

import { SurveyName } from '~/lib/constants/survey/SurveyName';
import { SurveyNameToDisplayNameMap } from '~/lib/constants/survey/SurveyNameToDisplayNameMap';
import { SurveyNameToSurveyKeyMap } from '~/lib/constants/survey/SurveyNameToSurveyKeyMap';
import { SurveyInstanceData } from '~/lib/types/assessments/SurveyInstanceData';
import { getPercentageDisplayLabel } from '~/lib/util/survey.util';

import AssessmentsView, { TableData } from './AssessmentsView';
import SurveyScoreBadge from '../../../common/SurveyScoreBadge';
import PatientDetailView from '../patient-details/PatientDetailView';
import Loading from '~/pages/loading';

type AssessmentsViewModalProps = {
  assessmentData: AllSurveyCardsData;
  clickedSurveyDetail: SurveyDetail;
  closeModal: () => void;
  id: string;
  patient: Patient;
  patientClinicalProfile?: PatientClinicalProfile;
  patientGender: string;
  patientPreferredFullName: string;
};
const { neutral } = EquipUIFireflyV2Theme.colors;
const { h6, label: labelStyle, paragraph } = EquipUITheme.typography;

export type SurveyCardProps = {
  displayIndex: number;
  id: string;
  patientGender?: string;
  surveyData: SurveyInstanceData;
};

const SurveyCard = ({
  displayIndex,
  id,
  surveyData,
  patientGender,
}: SurveyCardProps) => {
  const {
    completionPercentage,
    dateDisplayLabel,
    jsonResults,
    score,
    status,
    surveyName,
  } = surveyData;
  const title = SurveyNameToDisplayNameMap[surveyName];

  return (
    <ChakraBox
      border={`1px solid ${neutral.line}`}
      borderRadius="16px"
      id={id}
      marginTop="10px"
      minWidth="100px"
      padding="10px"
    >
      <ChakraFlex alignItems="center" justifyContent="space-between">
        <ChakraText
          {...h6}
          color="neutral.primary"
          fontWeight="bold"
          id={`${id}-title`}
        >
          {DateTime.fromFormat(dateDisplayLabel, 'LL/dd').toFormat('dd   LLLL')}
        </ChakraText>
        <SurveyScoreBadge
          id={`${id}-score-badge`}
          jsonResults={jsonResults}
          patientGender={patientGender}
          score={score}
          surveyName={surveyName as SurveyName}
        />
      </ChakraFlex>

      {score ? (
        <ChakraText {...paragraph} id={`${id}-score`} lineHeight="16.8px">
          {`Score ${score}`}
        </ChakraText>
      ) : (
        <ChakraText
          {...paragraph}
          id={`${id}-completion-percentage`}
          lineHeight="16.8px"
        >
          {getPercentageDisplayLabel(
            completionPercentage,
            status as AssessmentDisplayStatus,
            'Completed',
          )}
        </ChakraText>
      )}
      <ChakraText
        {...labelStyle}
        color="neutral.secondary"
        id={`${id}-survey-number`}
        overflow="visible"
        width="max-content"
      >
        ID number {displayIndex}
      </ChakraText>
      <ChakraText
        {...labelStyle}
        color="neutral.secondary"
        id={`${id}-submitted-datetime`}
        marginTop="6px"
        overflow="visible"
        width="max-content"
      >
        {title}
      </ChakraText>
    </ChakraBox>
  );
};

/** Helper to get the clicked survey instance and its three most recent survey instances*/
export const getRecentSurveys = (
  clickedSurveyDetail: SurveyDetail,
  assessmentsData: AllSurveyCardsData,
): SurveyCardProps[] => {
  if (!clickedSurveyDetail || !assessmentsData) return null;
  const { externalId: id, surveyName } = clickedSurveyDetail;
  const clickedSurveyObj =
    assessmentsData[SurveyNameToSurveyKeyMap[surveyName]];
  const { surveys, totalNumOfSurveys } = clickedSurveyObj;

  const clickedSurveyIndex = (surveys ?? []).findIndex(
    ({ externalId }) => id === externalId,
  );

  let i = clickedSurveyIndex;
  const recentThreeInstances = [];
  while (i >= 0 && recentThreeInstances.length < 3) {
    recentThreeInstances.push({
      displayIndex: totalNumOfSurveys - i + 1, // -i is to display index in descending order and +1 is to start the index from 1
      surveyData: surveys[i],
    });
    i -= 1;
  }
  return recentThreeInstances;
};

const getCardContent = (
  clickedSurveyDetail: SurveyDetail,
  assessmentsData: AllSurveyCardsData,
  id: string,
  patientGender: string,
) => {
  if (!clickedSurveyDetail) return null;
  const recentThreeInstances = getRecentSurveys(
    clickedSurveyDetail,
    assessmentsData,
  );

  return (
    <>
      {recentThreeInstances.map(({ surveyData, displayIndex }) => (
        <SurveyCard
          displayIndex={displayIndex}
          id={`${id}-${displayIndex}`}
          key={`${id}-${displayIndex}`}
          patientGender={patientGender}
          surveyData={surveyData}
        />
      ))}
    </>
  );
};

const getTableData = (
  assessmentData: AllSurveyCardsData,
  surveyDetail: SurveyDetail,
): TableData => {
  if (!surveyDetail) return null;
  const { surveyName } = surveyDetail;
  const clickedSurveyObj = assessmentData[SurveyNameToSurveyKeyMap[surveyName]];
  const recentThreeInstances = getRecentSurveys(surveyDetail, assessmentData);
  const { surveyData: clickedSurveyData, displayIndex } =
    recentThreeInstances[0];
  return {
    clickedSurveyData,
    lastFiveSurveyData: clickedSurveyObj.surveys?.slice(0, 5).reverse(),
    surveyIndex: displayIndex,
  };
};

const AssessmentsViewModal = ({
  assessmentData,
  clickedSurveyDetail,
  closeModal,
  id,
  patient,
  patientClinicalProfile,
  patientGender,
  patientPreferredFullName,
}: AssessmentsViewModalProps) => {
  return (
    <Modal
      footerContent={null}
      hideCloseButton
      hideHeader
      id={`${id}__view-modal`}
      isOpen={Boolean(clickedSurveyDetail)}
      modalBodyPadding="0"
      modalContentPadding="0"
      onClose={closeModal}
      size="full"
      title={null}
    >
      {!patientClinicalProfile && <Loading />}
      {patientClinicalProfile && (
        <ChakraFlex id={`${id}__view-container`} minHeight="100vh">
          <PatientDetailView
            cards={[
              getCardContent(
                clickedSurveyDetail,
                assessmentData,
                `${id}__patient-card`,
                patientGender,
              ),
            ]}
            id={`${id}__patient`}
            patient={patient}
            patientClinicalProfile={patientClinicalProfile}
            patientPreferredFullName={patientPreferredFullName}
          />
          <AssessmentsView
            closeModal={closeModal}
            id={`${id}__table-results`}
            tableData={getTableData(assessmentData, clickedSurveyDetail)}
          />
        </ChakraFlex>
      )}
    </Modal>
  );
};

export default AssessmentsViewModal;
